
import { Component, ChangeDetectionStrategy, input, output, effect, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AppUser } from '../../models/index';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { UserService } from '../../services/user.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-employee-form-modal',
  standalone: true,
  templateUrl: './employee-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe],
})
export class EmployeeFormModalComponent implements AfterViewInit {
  user = input<AppUser | null>();
  isOpen = input.required<boolean>();
  
  closeModal = output<void>();
  saveUser = output<AppUser>();

  userForm: FormGroup;
  userRoles: AppUser['role'][] = ['owner', 'admin', 'cashier', 'staff', 'doctor', 'accountant'];
  userStatuses: AppUser['status'][] = ['active', 'inactive'];

  // Signature Pad
  @ViewChild('signaturePad') signaturePadElement!: ElementRef<HTMLCanvasElement>;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);

  // Computed signal substitute
  get isDoctor() {
     return this.userForm.get('role')?.value === 'doctor' || this.userForm.get('role')?.value === 'admin' || this.userForm.get('role')?.value === 'owner';
  }

  // Upload/Preview State
  signaturePreview = ''; // Can't use signal here easily due to template ref limitations in simple setup, using property binding
  
  constructor() {
    const fb: FormBuilder = inject(FormBuilder);
    this.userForm = fb.group({
      uid: [null],
      name: ['', Validators.required],
      email: [{ value: '', disabled: true }, Validators.required],
      role: ['staff', Validators.required],
      status: ['active', Validators.required],
      pin: ['', [Validators.minLength(6), Validators.maxLength(6), Validators.pattern(/^\d{6}$/)]],
      signatureUrl: ['']
    });

    effect(() => {
        const userData = this.user();
        if (userData) {
            this.userForm.patchValue(userData);
            if(userData.role === 'admin' || userData.role === 'owner') {
                // Owner/Admin role change restricted, but signature allowed
                // We keep role disabled to prevent lockout, but check if we need to enable it for super admin
                // For now, keep disabled.
            }
            this.signaturePreview = userData.signatureUrl || '';
            
            // Re-init canvas if doctor
            setTimeout(() => this.initCanvas(), 200);

        } else {
            this.userForm.reset({
                uid: null,
                name: '',
                email: '',
                role: 'staff',
                status: 'active',
                pin: '',
                signatureUrl: ''
            });
            this.signaturePreview = '';
        }
    });
  }

  ngAfterViewInit() {
      this.initCanvas();
  }

  // --- Signature Logic ---
  initCanvas() {
    if (!this.signaturePadElement) return;
    const canvas = this.signaturePadElement.nativeElement;
    this.canvasContext = canvas.getContext('2d');
    
    if (this.canvasContext) {
      this.canvasContext.lineWidth = 2;
      this.canvasContext.lineCap = 'round';
      this.canvasContext.strokeStyle = 'blue';
      
      // Clear before use
      this.canvasContext.clearRect(0, 0, canvas.width, canvas.height);
    }

    // Add Events
    canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    canvas.addEventListener('mousemove', (e) => this.draw(e));
    canvas.addEventListener('mouseup', () => this.stopDrawing());
    canvas.addEventListener('mouseout', () => this.stopDrawing());
    canvas.addEventListener('touchstart', (e) => this.startDrawing(e), { passive: false });
    canvas.addEventListener('touchmove', (e) => this.draw(e), { passive: false });
    canvas.addEventListener('touchend', () => this.stopDrawing());
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.canvasContext?.beginPath();
    this.canvasContext?.moveTo(pos.x, pos.y);
    event.preventDefault();
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    const pos = this.getEventPos(event);
    this.canvasContext?.lineTo(pos.x, pos.y);
    this.canvasContext?.stroke();
    event.preventDefault();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.canvasContext?.closePath();
  }

  getEventPos(event: MouseEvent | TouchEvent) {
    const canvas = this.signaturePadElement.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  clearSignature() {
    if (this.signaturePadElement) {
        const canvas = this.signaturePadElement.nativeElement;
        this.canvasContext?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveSignatureFromPad() {
     const canvas = this.signaturePadElement.nativeElement;
     // Check empty
     const blank = document.createElement('canvas');
     blank.width = canvas.width;
     blank.height = canvas.height;
     if(canvas.toDataURL() === blank.toDataURL()) {
         this.notificationService.showWarning("Signature pad is empty.");
         return;
     }
     
     const dataUrl = canvas.toDataURL('image/png');
     await this.processAndUploadSignature(dataUrl);
  }

  // --- Upload Logic ---
  onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
          const file = input.files[0];
          const reader = new FileReader();
          reader.onload = (e) => {
              const result = e.target?.result as string;
              this.processAndUploadSignature(result);
          };
          reader.readAsDataURL(file);
      }
  }
  
  async processAndUploadSignature(base64: string) {
      const uid = this.userForm.get('uid')?.value;
      if (!uid) {
          this.notificationService.showError("Cannot upload signature for new user. Please create user first.");
          return;
      }
      
      try {
          const url = await this.userService.uploadUserSignature(uid, base64);
          this.userForm.patchValue({ signatureUrl: url });
          this.signaturePreview = url;
          this.notificationService.showSuccess("Signature uploaded successfully!");
          this.clearSignature();
      } catch (e: any) {
          this.notificationService.showError("Upload failed: " + e.message);
      }
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSave(): void {
    if (this.userForm.valid) {
      this.saveUser.emit(this.userForm.getRawValue());
    }
  }

  onBackdropClick(event: MouseEvent): void {
      if (event.target === event.currentTarget) {
          this.onClose();
      }
  }
}
